import { useEffect, useState } from "react";
import { FALLBACK_LANGUAGES, type Language } from "@/lib/languages.ts";

export type Health = {
  ok: boolean;
  version?: string;
  /** AI engine new live sessions use: "soniox" (real time) or "cloud". */
  aiEngine?: "soniox" | "cloud";
  provider?: { name: string; api?: string; model?: string; reachable?: boolean; modelPresent?: boolean | null; error?: string };
  activeSessions?: number;
  maxSessions?: number;
  refineTranslation?: boolean;
  accessCodeRequired?: boolean;
  maxAttendeeLanguages?: number;
  voices?: string[];
  listenerLanguages?: string[];
  retention?: string;
};

export type EngineStatus = "unknown" | "ok" | "degraded" | "down";

export function useEngineInfo(pollMs = 15000) {
  const [health, setHealth] = useState<Health | null>(null);
  const [status, setStatus] = useState<EngineStatus>("unknown");
  const [languages, setLanguages] = useState<Language[]>(FALLBACK_LANGUAGES);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch("/engine/health", { cache: "no-store" });
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as Health;
        if (cancelled) return;
        setHealth(body);
        const p = body.provider;
        setStatus(p?.reachable && p.modelPresent !== false ? "ok" : "degraded");
      } catch {
        if (!cancelled) setStatus("down");
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), pollMs);
    fetch("/engine/languages")
      .then((r) => (r.ok ? r.json() : null))
      .then((body: { languages?: Language[] } | null) => {
        if (!cancelled && body?.languages?.length) setLanguages(body.languages);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [pollMs]);

  return { health, status, languages };
}
